import {
  IssuePlacementPinUpsert,
  SerializedContext,
} from "../utils/types/PlacementViewer";
import { getPlacementType } from "../utils/data/getPlacementType";
import {
  IssuePlacementPin,
  IssuePlacementType,
  IssuePlacementWithPins,
  PlacementType,
} from "@adsk/acc-issues-types";
import { useCallback } from "react";
import isEmpty from "lodash/isEmpty";
import {
  AccDocumentSurface,
  DocumentViewable,
  SheetViewable,
} from "@adsk/feature-markups";
import { CommonUtil } from "@adsk/docs-js-sdk";

type GenerateIssuePlacements = (
  context: SerializedContext,
  pins: IssuePlacementPinUpsert[],
  createdAtVersion?: number
) => IssuePlacementWithPins[];

const transformToIssuePlacementPins = (
  pins: IssuePlacementPinUpsert[],
  placementType: PlacementType,
  bubbleUrn: string | null
): IssuePlacementPin[] => {
  if (placementType !== PlacementType.SheetView && bubbleUrn === null) {
    throw new Error(
      "getIssuePlacementPins - bubbleUrn cannot be null for file view placement"
    );
  }

  return pins
    .map((p): IssuePlacementPin | undefined => {
      switch (placementType) {
        case PlacementType.SheetView:
          return {
            uid: p.uid ?? undefined,
            position: p.position,
            z: 0,
          };
        case PlacementType.ThreeDFileView:
          return {
            ...p,
            position: p.position,
            viewableInfo: {
              documentVersionUrn: bubbleUrn!,
            },
          };
        case PlacementType.TwoDFileView:
          return {
            uid: p.uid ?? undefined,
            position: p.position,
            viewableInfo: {
              documentVersionUrn: bubbleUrn!,
            },
          };
        default:
          return undefined;
      }
    })
    .filter((p): p is IssuePlacementPin => p !== undefined);
};

export const useGenerateIssuePlacements = (): GenerateIssuePlacements => {
  return useCallback(
    (
      context: SerializedContext,
      pins: IssuePlacementPinUpsert[],
      issueCreatedAtFileVersion?: number
    ) => {
      const { surface, versionNumber, currentViewable } = context;
      const placementType = getPlacementType(pins[0], context);
      const is3D = placementType === PlacementType.ThreeDFileView;
      const isSheet = placementType === PlacementType.SheetView;
      const lineageUrn = (currentViewable as DocumentViewable).lineageUrn ?? "";
      const createdAtVersion = issueCreatedAtFileVersion ?? versionNumber ?? 1;
      // Always use current versionNumber for bubbleUrn since every document version has its own markup
      const bubbleUrn = CommonUtil.lineage2Version(
        lineageUrn,
        versionNumber ?? 1
      );

      const issuePlacementPins = transformToIssuePlacementPins(
        pins,
        placementType,
        bubbleUrn
      );

      if (isEmpty(issuePlacementPins)) {
        return [];
      }

      const fileSurface = surface as AccDocumentSurface;
      const sheetViewable = currentViewable as SheetViewable;

      return [
        isSheet
          ? {
              type: IssuePlacementType.SHEET,
              sheetId: sheetViewable.sheetUid,
              viewable: {
                guid: sheetViewable.bubbleViewableGuid,
                name: sheetViewable.name,
              },
              pins: issuePlacementPins,
            }
          : {
              type: IssuePlacementType.FILE,
              viewable: {
                guid: fileSurface.viewableGuid,
                viewableId: fileSurface.viewableId,
                name: fileSurface.viewableName,
                is3D,
              },
              lineageUrn,
              createdAtVersion,
              pins: issuePlacementPins,
            },
      ];
    },
    []
  );
};
